/**
 * COI sketch API client: POST /v1/cois/sketch.
 *
 * The backend turns a text description into sketch ops via an LLM (or the
 * deterministic mock provider in dev), executes them, and returns a polygon
 * plus a human-readable step log. The frontend never sees coordinates from
 * the model — only the final executed polygon.
 */
import { ApiError, apiBaseUrl, apiFetch, fetchWithAuth, tenantHeaders } from './api';
import { getAccessToken } from './authToken';

export interface SketchTurn {
  description: string;
  ops: Record<string, unknown>[];
}

export interface SketchJurisdiction {
  name: string;
  /** [west, south, east, north] in EPSG:4326. */
  bbox: [number, number, number, number];
  boundary_geojson?: Record<string, unknown>;
}

export interface SketchResult {
  ops: Record<string, unknown>[];
  polygon_geojson: Record<string, unknown> | null;
  steps: string[];
  warnings: string[];
  notes: string[];
  provider: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

function toRecordList(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord);
}

function toSketchResult(payload: unknown): SketchResult {
  const record = isRecord(payload) ? payload : {};
  const polygon = isRecord(record.polygon_geojson) ? record.polygon_geojson : null;
  return {
    ops: toRecordList(record.ops),
    polygon_geojson: polygon,
    steps: toStringList(record.steps),
    warnings: toStringList(record.warnings),
    notes: toStringList(record.notes),
    provider: typeof record.provider === 'string' ? record.provider : 'unknown',
  };
}

export async function postSketch(
  description: string,
  history: SketchTurn[],
  jurisdiction: SketchJurisdiction,
): Promise<SketchResult> {
  const payload = await apiFetch<unknown>('/v1/cois/sketch', {
    method: 'POST',
    body: JSON.stringify({
      description,
      history: history.map((t) => ({ description: t.description, ops: t.ops })),
      jurisdiction,
    }),
  });
  return toSketchResult(payload);
}

/* ------------------------------------------------------------------ */
/* Submit API client: POST /v1/cois                                    */
/* ------------------------------------------------------------------ */

export interface CoiSketchProvenance {
  provider: string;
  steps: string[];
  warnings: string[];
  notes: string[];
}

export interface CoiSubmitInput {
  title?: string;
  narrative: string;
  interests?: string;
  /** GeoJSON Polygon/MultiPolygon, or null when the resident submits words only. */
  polygon_geojson: Record<string, unknown> | null;
  sketch?: CoiSketchProvenance | null;
  contact?: string;
}

export interface CoiReceipt {
  id: string;
  status: string;
  submitted_at: string | null;
}

export interface CoiFieldError {
  field: string;
  message: string;
}

/**
 * Thrown when the backend rejects a COI submission with 422. Carries the
 * field-level problems so the form can render them inline.
 */
export class CoiRejectedError extends Error {
  readonly fieldErrors: CoiFieldError[];

  constructor(fieldErrors: CoiFieldError[]) {
    super(
      fieldErrors.length > 0
        ? fieldErrors.map((e) => e.message).join(' ')
        : 'The submission was rejected.',
    );
    this.name = 'CoiRejectedError';
    this.fieldErrors = fieldErrors;
  }
}

function toFieldErrors(value: unknown): CoiFieldError[] {
  if (!Array.isArray(value)) return [];
  const out: CoiFieldError[] = [];
  for (const item of value) {
    if (!isRecord(item)) continue;
    const field = typeof item.field === 'string' ? item.field : 'narrative';
    const message = typeof item.message === 'string' ? item.message : null;
    // FastAPI's own request-validation shape: {loc: [...], msg, type}.
    // Extract the last string-ish loc segment without touching `any`.
    let locField: string | null = null;
    const locRaw: unknown = item.loc;
    if (Array.isArray(locRaw)) {
      for (const part of locRaw) {
        if (typeof part === 'string' || typeof part === 'number') {
          locField = String(part);
        }
      }
    }
    const msg = typeof item.msg === 'string' ? item.msg : null;
    if (message) {
      out.push({ field, message });
    } else if (msg && locField) {
      out.push({ field: locField, message: msg });
    }
  }
  return out;
}

function toCoiReceipt(payload: unknown, fallbackId: string): CoiReceipt {
  const record = isRecord(payload) ? payload : {};
  const id = typeof record.id === 'string' ? record.id : fallbackId;
  return {
    id,
    status: typeof record.status === 'string' ? record.status : 'received',
    submitted_at: typeof record.submitted_at === 'string' ? record.submitted_at : null,
  };
}

/**
 * POST /v1/cois — public endpoint, no login required.
 *
 * Throws CoiRejectedError on 422 (with field-level messages), ApiError
 * otherwise (including a friendly message for 429 rate limiting).
 */
export async function submitCoi(input: CoiSubmitInput): Promise<CoiReceipt> {
  let response: Response;
  try {
    response = await fetchWithAuth(`${apiBaseUrl()}/v1/cois`, {
      method: 'POST',
      body: JSON.stringify({
        title: input.title?.trim() || null,
        narrative: input.narrative,
        interests: input.interests?.trim() || null,
        polygon_geojson: input.polygon_geojson,
        sketch_provenance: input.sketch ?? null,
        contact: input.contact?.trim() || null,
      }),
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Network request failed';
    throw new ApiError(0, 'network_error', `Could not reach the backend: ${message}`);
  }

  const text = await response.text();
  let body: unknown;
  try {
    body = text ? (JSON.parse(text) as unknown) : undefined;
  } catch {
    throw new ApiError(response.status, 'invalid_json', 'Backend returned a non-JSON response body.');
  }

  if (response.status === 422) {
    const record = isRecord(body) ? body : {};
    const detail = isRecord(record.detail) ? record.detail : {};
    const errors = toFieldErrors(detail.errors ?? record.detail);
    throw new CoiRejectedError(
      errors.length > 0 ? errors : [{ field: 'narrative', message: 'The submission was rejected.' }],
    );
  }

  if (response.status === 429) {
    throw new ApiError(
      429,
      'rate_limited',
      'Too many submissions from this address — please wait a few minutes and try again.',
    );
  }

  if (!response.ok) {
    const record = isRecord(body) ? body : {};
    const detail = typeof record.detail === 'string' ? record.detail : response.statusText;
    throw new ApiError(response.status, `http_${response.status}`, detail || 'Request failed');
  }

  return toCoiReceipt(body, '');
}

/* ------------------------------------------------------------------ */
/* "My communities": the sign-in account's own COIs (drafts + submitted) */
/* ------------------------------------------------------------------ */

export interface MyCoiItem {
  id: string;
  title: string | null;
  narrative_excerpt: string;
  status: string;
  bbox: [number, number, number, number] | null;
  has_share_link: boolean;
  parent_coi_id: string | null;
  created_at: string;
}

export interface MyCoiDetail {
  id: string;
  title: string | null;
  narrative: string;
  interests: string | null;
  contact: string | null;
  polygon_geojson: Record<string, unknown>;
  sketch_provenance: CoiSketchProvenance | null;
  status: string;
  parent_coi_id: string | null;
  has_share_link: boolean;
  submitted_by: string | null;
  created_at: string;
}

export interface CoiDraftInput {
  title?: string;
  narrative: string;
  interests?: string;
  polygon_geojson: Record<string, unknown> | null;
  contact?: string;
  sketch_provenance?: CoiSketchProvenance | null;
}

function toStringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function toStringWithFallback(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function toBBox(value: unknown): [number, number, number, number] | null {
  if (
    Array.isArray(value) &&
    value.length === 4 &&
    value.every((v) => typeof v === 'number' && Number.isFinite(v))
  ) {
    return [value[0], value[1], value[2], value[3]];
  }
  return null;
}

function toSketchProvenance(value: unknown): CoiSketchProvenance | null {
  if (!isRecord(value)) return null;
  return {
    provider: typeof value.provider === 'string' ? value.provider : 'unknown',
    steps: toStringList(value.steps),
    warnings: toStringList(value.warnings),
    notes: toStringList(value.notes),
  };
}

function toMyCoiItem(value: unknown): MyCoiItem {
  const record = isRecord(value) ? value : {};
  return {
    id: toStringWithFallback(record.id, ''),
    title: toStringOrNull(record.title),
    narrative_excerpt: toStringWithFallback(record.narrative_excerpt, ''),
    status: toStringWithFallback(record.status, 'draft'),
    bbox: toBBox(record.bbox),
    has_share_link: record.has_share_link === true,
    parent_coi_id: toStringOrNull(record.parent_coi_id),
    created_at: toStringWithFallback(record.created_at, ''),
  };
}

function toMyCoiDetail(value: unknown): MyCoiDetail {
  const record = isRecord(value) ? value : {};
  return {
    id: toStringWithFallback(record.id, ''),
    title: toStringOrNull(record.title),
    narrative: toStringWithFallback(record.narrative, ''),
    interests: toStringOrNull(record.interests),
    contact: toStringOrNull(record.contact),
    polygon_geojson: isRecord(record.polygon_geojson) ? record.polygon_geojson : {},
    sketch_provenance: toSketchProvenance(record.sketch_provenance),
    status: toStringWithFallback(record.status, 'draft'),
    parent_coi_id: toStringOrNull(record.parent_coi_id),
    has_share_link: record.has_share_link === true,
    submitted_by: toStringOrNull(record.submitted_by),
    created_at: toStringWithFallback(record.created_at, ''),
  };
}

/**
 * Authed fetch mirroring submitCoi's raw-fetch shape (so 422 field errors
 * survive with their detail payload, which apiFetch would flatten).
 * Throws CoiRejectedError on 422, ApiError otherwise.
 */
async function authedCoiFetch(
  path: string,
  method: 'GET' | 'PUT' | 'POST',
  body?: Record<string, unknown>,
): Promise<unknown> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...tenantHeaders() };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl()}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Network request failed';
    throw new ApiError(0, 'network_error', `Could not reach the backend: ${message}`);
  }
  const text = await response.text();
  let parsed: unknown;
  try {
    parsed = text ? (JSON.parse(text) as unknown) : undefined;
  } catch {
    throw new ApiError(response.status, 'invalid_json', 'Backend returned a non-JSON response body.');
  }
  if (response.status === 422) {
    const record = isRecord(parsed) ? parsed : {};
    const detail = isRecord(record.detail) ? record.detail : {};
    const errors = toFieldErrors(detail.errors ?? record.detail);
    throw new CoiRejectedError(
      errors.length > 0 ? errors : [{ field: 'narrative', message: 'The request was rejected.' }],
    );
  }
  if (!response.ok) {
    const record = isRecord(parsed) ? parsed : {};
    const detail = typeof record.detail === 'string' ? record.detail : response.statusText;
    throw new ApiError(response.status, `http_${response.status}`, detail || 'Request failed');
  }
  return parsed;
}

/** GET /v1/cois/mine — the sign-in account's COIs (auth required). */
export async function listMyCois(): Promise<MyCoiItem[]> {
  const payload = await authedCoiFetch('/v1/cois/mine', 'GET');
  const record = isRecord(payload) ? payload : {};
  const items = Array.isArray(record.items) ? record.items : [];
  return items.map(toMyCoiItem);
}

/** GET /v1/cois/mine/{id} — one of the caller's COIs (auth required). */
export async function getMyCoi(coiId: string): Promise<MyCoiDetail> {
  const payload = await authedCoiFetch(`/v1/cois/mine/${encodeURIComponent(coiId)}`, 'GET');
  return toMyCoiDetail(payload);
}

/** POST /v1/cois/mine — save the form as a new draft in the account (auth required). */
export async function createMyCoiDraft(input: CoiDraftInput): Promise<MyCoiDetail> {
  const payload = await authedCoiFetch('/v1/cois/mine', 'POST', {
    title: input.title?.trim() || null,
    narrative: input.narrative,
    interests: input.interests?.trim() || null,
    polygon_geojson: input.polygon_geojson,
    contact: input.contact?.trim() || null,
    ...(input.sketch_provenance ? { sketch_provenance: input.sketch_provenance } : {}),
  });
  return toMyCoiDetail(payload);
}

/** PUT /v1/cois/mine/{id} — save edits to a draft (auth required). */
export async function updateMyCoiDraft(
  coiId: string,
  input: CoiDraftInput,
): Promise<MyCoiDetail> {
  const payload = await authedCoiFetch(`/v1/cois/mine/${encodeURIComponent(coiId)}`, 'PUT', {
    title: input.title?.trim() || null,
    narrative: input.narrative,
    interests: input.interests?.trim() || null,
    polygon_geojson: input.polygon_geojson,
    contact: input.contact?.trim() || null,
    ...(input.sketch_provenance ? { sketch_provenance: input.sketch_provenance } : {}),
  });
  return toMyCoiDetail(payload);
}

/** POST /v1/cois/mine/{id}/submit — submit a draft (auth required). */
export async function submitMyCoiDraft(coiId: string): Promise<CoiReceipt> {
  const payload = await authedCoiFetch(
    `/v1/cois/mine/${encodeURIComponent(coiId)}/submit`,
    'POST',
  );
  return toCoiReceipt(payload, coiId);
}
